/**
 * Conservation de l'état du repli d'un chargement de page à l'autre. Face à un gel, le spectateur
 * recharge : sans mémoire, il retombe sur le flux qui vient de geler et le repli ne part jamais.
 *
 * `sessionStorage`, donc par onglet. Tout échec de lecture ou d'écriture (navigation privée, quota)
 * ramène au comportement sans mémoire, jamais à une erreur.
 */

import type { PlaybackStreamQuality } from '@/features/live/types';
import { createDowngradeState, type DowngradeState } from '@/lib/streamDowngrade';
import { QUALITY_ORDER } from '@/lib/streamSelection';

const STORAGE_KEY = 'welya.streamDowngrade';

/** Au-delà, les conditions du live ont changé et l'état ne décrit plus rien d'utile. */
export const DOWNGRADE_STATE_TTL_MS = 30 * 60_000;

export type DowngradeStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** Le simple accès à `sessionStorage` lève dans certains navigateurs quand le stockage est bloqué. */
export function sessionStorageOrNull(): DowngradeStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

const isQuality = (value: unknown): value is PlaybackStreamQuality =>
  QUALITY_ORDER.includes(value as PlaybackStreamQuality);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** Relit un état écrit par une autre version du code sans jamais lui faire confiance. */
function parseState(raw: unknown): DowngradeState | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const state = raw as Record<string, unknown>;
  if (
    !Array.isArray(state.avoided) ||
    !state.avoided.every(isQuality) ||
    !(state.bandwidthCeiling === null || isQuality(state.bandwidthCeiling)) ||
    !isFiniteNumber(state.downgradeCount) ||
    !Array.isArray(state.recentStallsMs) ||
    !state.recentStallsMs.every(isFiniteNumber) ||
    !isFiniteNumber(state.lastChangeAtMs) ||
    !(state.streamDeadAtMs === null || isFiniteNumber(state.streamDeadAtMs))
  ) {
    return null;
  }
  return {
    ...createDowngradeState(),
    avoided: state.avoided,
    bandwidthCeiling: state.bandwidthCeiling,
    downgradeCount: state.downgradeCount,
    recentStallsMs: state.recentStallsMs,
    lastChangeAtMs: state.lastChangeAtMs,
    streamDeadAtMs: state.streamDeadAtMs,
  };
}

/** `null` quand rien d'exploitable n'a été conservé pour cette session de live. */
export function loadDowngradeState(
  storage: DowngradeStorage | null,
  sessionIdentifier: string | undefined,
  nowMs: number,
): DowngradeState | null {
  if (storage === null || !sessionIdentifier) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const stored = JSON.parse(raw) as Record<string, unknown>;
    if (stored.sessionIdentifier !== sessionIdentifier) return null;
    if (!isFiniteNumber(stored.savedAtMs)) return null;
    const ageMs = nowMs - stored.savedAtMs;
    if (ageMs < 0 || ageMs > DOWNGRADE_STATE_TTL_MS) return null;
    return parseState(stored.state);
  } catch {
    return null;
  }
}

/** Une seule entrée par onglet : ouvrir un autre live l'écrase, rien ne s'accumule. */
export function saveDowngradeState(
  storage: DowngradeStorage | null,
  sessionIdentifier: string | undefined,
  state: DowngradeState,
  nowMs: number,
): void {
  if (storage === null || !sessionIdentifier) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({ sessionIdentifier, savedAtMs: nowMs, state }));
  } catch {
    // Quota ou stockage bloqué : le repli continue en mémoire, comme avant.
  }
}
